import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { getAgencyPublicData } from '../services/agencyPublicService';
import type { AgencyPublicData } from '../services/agencyPublicService';
import api from '../services/api';

const ForgotPassword = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const location = useLocation();
  const resolvedSlug = useMemo(() => {
    const search = new URLSearchParams(location.search);
    const qsSlug = search.get('agency') || search.get('agencySlug');
    if (agencySlug && agencySlug !== 'checkin') return agencySlug;
    if (qsSlug) return qsSlug;
    return '';
  }, [agencySlug, location.search]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [agencyData, setAgencyData] = useState<AgencyPublicData | null>(null);
  const [fetchingAgency, setFetchingAgency] = useState(true);
  const [agencyError, setAgencyError] = useState('');

  useEffect(() => {
    const fetchAgencyData = async () => {
      if (!resolvedSlug) {
        setAgencyError('Agency not specified');
        setFetchingAgency(false);
        return;
      }

      try {
        const data = await getAgencyPublicData(resolvedSlug);
        setAgencyData(data);
      } catch (err: any) {
        console.error('Failed to fetch agency data:', err);
        setAgencyError('Agency not found');
      } finally {
        setFetchingAgency(false);
      }
    };

    fetchAgencyData();
  }, [resolvedSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolvedSlug) {
      setError('Agency not specified');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/auth/forgot-password', { email, agencySlug: resolvedSlug });
      setSuccess('If your email is registered, you will receive a password reset link.');
      setEmail(''); // Clear the email field
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send password reset email.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingAgency) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  if (agencyError || !agencyData) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Alert severity="error" sx={{ width: '100%' }}>
              {agencyError || 'Agency not found'}
            </Alert>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Agency Banner */}
          {agencyData.bannerImageData && agencyData.bannerImageType && (
            <Box
              sx={{
                width: '100%',
                mb: 3,
                borderRadius: 1,
                overflow: 'hidden',
                maxHeight: '200px',
              }}
            >
              <img
                src={`data:${agencyData.bannerImageType};base64,${agencyData.bannerImageData}`}
                alt={`${agencyData.name} banner`}
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}

          <Typography component="h1" variant="h5">
            Forgot Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, textAlign: 'center' }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
              {success}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !!success}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                href={`/${resolvedSlug}`}
                variant="text"
                size="small"
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;

